"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Show, UserButton } from "@clerk/nextjs";
import AuthSignInButton from "@/components/AuthSignInButton";
import { IconBolt, IconChevronDown } from "@tabler/icons-react";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";

import {
  ActionIcon,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
} from "@mantine/core";
import MobileNavbarSearch from "@/components/MobileNavBarSearch";
import FavoriteButton from "@/components/FavoriteButton";
import { SlideIn } from "@/components/SlideIn";

const NotificationBell = dynamic(() => import("@/components/NotificationBell"));
import { useMobileHistoryState } from "@/hooks/useMobileHistoryState";
import useModalFocusAndScrollLock from "@/hooks/useModalFocusAndScrollLock";
import { useAuthContext } from "@/context/authContext";

import { navLinks, socialLinks, infoLinks, analyticsReports } from "@/lib/shared/constants";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";

import Link from "@/components/Link";
import SocialIcon from "@/components/layout/SocialIcon";

import styles from "./styles.module.scss";

const RenderLink = (to: string, label: string, pathname: string, featured?: boolean) => {
  const isActive = pathname === to || (to !== "/" && pathname.startsWith(`${to}/`));

  return (
    <Link
      key={to}
      href={to}
      className={styles.navlink}
      data-featured={featured}
      data-active={isActive}
      prefetch={false}
    >
      {label}
    </Link>
  );
};

type MobileSubnav = "info" | "reports" | null;

const MobileMenu = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { isSignedIn, isTester, isProUser } = useAuthContext();
  const tokensRemaining = useTokenBudgetStore((s) => s.tokensRemaining);
  const tokensLimit = useTokenBudgetStore((s) => s.tokensLimit);
  const tokenInitialized = useTokenBudgetStore((s) => s.initialized);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(pathname);
  const isHomePage = pathname === "/";
  const isSearchPage = pathname.includes("/search") && !pathname.includes("/search/product/");
  const defaultSubnav: MobileSubnav = pathname.startsWith("/reports/")
    ? "reports"
    : infoLinks.some(({ url }) => pathname === url || pathname.startsWith(`${url}/`))
      ? "info"
      : null;
  const [openSubnav, setOpenSubnav] = useState<MobileSubnav>(defaultSubnav);

  useEffect(() => {
    if (currentRoute !== pathname) {
      setCurrentRoute(pathname);
      setMenuOpen(false);
      setSearchOpen(false);
      setOpenSubnav(defaultSubnav);
    }
  }, [currentRoute, defaultSubnav, pathname]);

  const toggleMenu = () => {
    setMenuOpen((open) => !open);
    if (!menuOpen && searchOpen) {
      setSearchOpen(false);
    }
  };

  const toggleSearch = () => {
    setSearchOpen((open) => !open);
    if (!searchOpen && menuOpen) {
      setMenuOpen(false);
    }
  };

  const toggleSubnav = (section: Exclude<MobileSubnav, null>) => {
    setOpenSubnav((current) => (current === section ? null : section));
  };

  useEffect(() => {
    if (!searchOpen || !isSearchPage) return;

    const handleScroll = () => setSearchOpen(false);
    const timer = setTimeout(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }, 400);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [searchOpen, isSearchPage]);

  const closeMenu = () => setMenuOpen(false);
  const focusTrapRef = useModalFocusAndScrollLock({
    isOpen: menuOpen,
    shouldTrapFocus: menuOpen,
    shouldLockScroll: menuOpen,
  });

  useMobileHistoryState({
    isOpen: menuOpen,
    onClose: closeMenu,
    modalId: "mobile-nav-menu",
  });

  return (
    <nav
      id="mobileNav"
      data-static={pathname === "/"}
      data-search={pathname.includes("search") && !pathname.includes("product")}
      className={`${styles.navBar} ${styles.mobileNav}`}
    >
      <MantineBox className={styles.leftSection}>
        <Link href="/" className={styles.logo}>
          <MantineImage
            src="/K-Logo-80.webp"
            alt="KURB"
            width={80}
            height={80}
            w={40}
            h={40}
            fit="contain"
          />
        </Link>
      </MantineBox>

      <MantineBox className={styles.mobileButtonContainer}>
        {!isHomePage && <MobileNavbarSearch isOpen={searchOpen} onToggle={toggleSearch} />}
        <Show when="signed-in">
          <MantineBox className={styles.signInButton}>
            <NotificationBell />
            <FavoriteButton size={26} showCount={false} forceShow />
            <UserButton userProfileUrl="/profile" userProfileMode="navigation">
              <UserButton.MenuItems>
                {TOKEN_UI_ENABLED && (
                  <UserButton.Action
                    label={
                      tokenInitialized
                        ? `${tokensRemaining} / ${tokensLimit} tokens today`
                        : "Daily tokens"
                    }
                    labelIcon={<IconBolt size={16} />}
                    onClick={() => router.push("/profile/billing")}
                  />
                )}
              </UserButton.MenuItems>
            </UserButton>
            &nbsp;
          </MantineBox>
        </Show>
        <Show when="signed-out">
          <MantineBox className={styles.signInButton}>
            <AuthSignInButton>
              {/* eslint-disable-next-line no-restricted-syntax -- Clerk must clone a native element. */}
              <button className={styles.signInHeaderBtn}>SIGN IN</button>
            </AuthSignInButton>
          </MantineBox>
        </Show>
        <ActionIcon
          onClick={toggleMenu}
          className={styles.hamburgerIcon}
          variant="transparent"
          size={44}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-menu"
        >
          <MantineImage src="/hamburger.svg" alt="" aria-hidden="true" w={24} h={24} />
        </ActionIcon>
      </MantineBox>

      <SlideIn
        active={menuOpen}
        onClose={closeMenu}
        side="right"
        width="medium"
        fullWidthAt="small"
        offsetTop={false}
        overflow="auto"
        padding="mobile"
        animate={false}
        title="MENU"
        modal
        modalLabel="Main navigation"
        backdropLabel="Close navigation menu"
        id="mobile-nav-menu"
        ref={focusTrapRef}
        className={styles.mobileMenuSheet}
      >
        <MantineBox className={styles.mobileMenuContent} c="black">
          <MantineBox className={styles.links}>
            <Show when="signed-out">
              <AuthSignInButton>
                {/* eslint-disable-next-line no-restricted-syntax -- Clerk must clone a native element. */}
                <button className={styles.signInLink}>SIGN IN</button>
              </AuthSignInButton>
            </Show>
            {navLinks.map(({ name, url, featured }) => RenderLink(url, name, pathname, featured))}

            <MantineBox className={styles.subnavSection}>
              <button
                type="button"
                className={styles.subnavTrigger}
                data-active={defaultSubnav === "info"}
                aria-label="Mobile INFO navigation"
                aria-expanded={openSubnav === "info"}
                aria-controls="mobile-info-links"
                onClick={() => toggleSubnav("info")}
              >
                <span>INFO</span>
                <IconChevronDown
                  className={styles.subnavChevron}
                  data-open={openSubnav === "info"}
                  size={20}
                  aria-hidden="true"
                />
              </button>
              {openSubnav === "info" && (
                <MantineBox id="mobile-info-links" className={styles.subnavLinks}>
                  {infoLinks.map(({ name, url }) => RenderLink(url, name, pathname))}
                </MantineBox>
              )}
            </MantineBox>

            <MantineBox className={styles.subnavSection}>
              <button
                type="button"
                className={styles.subnavTrigger}
                data-active={defaultSubnav === "reports"}
                aria-label="Mobile REPORTS navigation"
                aria-expanded={openSubnav === "reports"}
                aria-controls="mobile-report-links"
                onClick={() => toggleSubnav("reports")}
              >
                <span>REPORTS</span>
                <IconChevronDown
                  className={styles.subnavChevron}
                  data-open={openSubnav === "reports"}
                  size={20}
                  aria-hidden="true"
                />
              </button>
              {openSubnav === "reports" && (
                <MantineBox id="mobile-report-links" className={styles.subnavLinks}>
                  {analyticsReports.map(({ label, slug }) =>
                    RenderLink(`/reports/${slug}`, label, pathname)
                  )}
                </MantineBox>
              )}
            </MantineBox>

            {(isTester || isProUser) && RenderLink("/appraise", "APPRAISE", pathname, true)}
          </MantineBox>
          <MantineBox className={styles.menuFooter}>
            {isSignedIn && RenderLink("/profile", "USER SETTINGS", pathname)}
            {RenderLink("/privacy", "PRIVACY", pathname)}
            {RenderLink("/terms", "TERMS", pathname)}
            <MantineBox className={styles.socialLinks}>
              {socialLinks.map(({ name, url }) => (
                <MantineAnchor
                  key={name}
                  href={url.startsWith("http") ? url : `https://${url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                >
                  <SocialIcon name={name} size={22} stroke={1.35} />
                </MantineAnchor>
              ))}
            </MantineBox>
          </MantineBox>
        </MantineBox>
      </SlideIn>
    </nav>
  );
};

export default MobileMenu;
